import { backendServer } from "../../variables";
import moment from "moment";
import { LoginAction } from "./LoginActions";

// Utility to update stats for a value
function updateStats(stats, value) {
  if (value === undefined || value === null || isNaN(value)) return;
  stats.mean.push(value);
  stats.sum += value;
  if (value > stats.max) stats.max = value;
  if (value < stats.min) stats.min = value;
}

// Utility to finalize stats (average and median)
function finalizeStats(stats, length, roundMedian = false) {
  stats.avg = length > 0 ? stats.sum / length : 0;
  stats.mean.sort((a, b) => a - b);
  const lowMiddle = Math.floor((stats.mean.length - 1) / 2);
  const highMiddle = Math.ceil((stats.mean.length - 1) / 2);
  let median = (stats.mean[lowMiddle] + stats.mean[highMiddle]) / 2;
  stats.mean = roundMedian ? Math.round((median + Number.EPSILON) * 100) / 100 : median;
}

const setBusyState = (newBusyState, dispatch) => {
  dispatch({
    type: "SET_DATA",
    payload: {
      busy: newBusyState,
    },
  });
};

const toggleDetailedValues = () => {
  return (dispatch, getState) => {
    let { showDetailedValues } = getState().dataState;
    dispatch({
      type: "SET_DATA",
      payload: {
        showDetailedValues: !showDetailedValues,
      },
    });
  };
};

const getDevices = () => {
  return async (dispatch, getState) => {
    let { token } = getState().loginState;
    let res = await fetch(backendServer + "getdevices", {
      method: "GET",
      headers: {
        "x-access-token": token,
      },
    });
    if (res.status === 200) {
      let data = await res.json();
      dispatch({
        type: "SET_DEVICES",
        payload: {
          data: data,
        },
      });
    } else {
      if (res.status >= 300 && res.status < 400) {
        dispatch({
          type: "LOGOUT",
        });
      }
    }
  };
};

const SENSOR_KEYS = [
  { key: "co2", parse: parseInt },
  { key: "humidity", parse: parseInt },
  { key: "humidity2", parse: parseInt },
  { key: "temperature", parse: parseFloat },
  { key: "temperature2", parse: parseFloat },
  { key: "pressure", parse: parseFloat },
  { key: "power", parse: parseFloat },
  { key: "voltage", parse: parseFloat },
  { key: "current", parse: parseFloat },
  { key: "Compressor speed RPM", parse: parseInt },
  { key: "Brine in", parse: parseFloat },
  { key: "Brine out", parse: parseFloat },
  { key: "Discharge pipe temperature", parse: parseFloat },
  { key: "Outdoor temperature", parse: parseFloat },
  { key: "Tap water top", parse: parseFloat },
  { key: "Tap water bottom", parse: parseFloat },
];

const updateChart = async (deviceId, dispatch, getState) => {
  const { fromDate, toDate, sensors } = getState().filterState;
  if (sensors[deviceId] === false) {
    return;
  }
  // Parse fromDate and toDate if they are strings
  const parsedFromDate = typeof fromDate === "string" ? moment(fromDate, "D.M.YYYY HH.mm") : fromDate;
  const parsedToDate = typeof toDate === "string" ? moment(toDate, "D.M.YYYY HH.mm") : toDate;
  const fromDateValue = parsedFromDate.valueOf();
  const toDateValue = parsedToDate.valueOf();
  setBusyState(true, dispatch);
  const { token } = getState().loginState;
  let res = await fetch(backendServer + "getDeviceData", {
    method: "POST",
    "Content-Type": "application/json",
    headers: {
      "x-access-token": token,
    },
    body: JSON.stringify({
      deviceId,
      fromDate: fromDateValue,
      toDate: toDateValue,
    }),
  });
  if (res.status === 200) {
    let data = await res.json();
    if (Array.isArray(data)) {
      data = JSON.parse(JSON.stringify(data));
      // Initialize stats objects for each sensor
      const stats = {};
      SENSOR_KEYS.forEach(({ key }) => {
        stats[key] = { sum: 0, min: 10000, max: -10000, mean: [] };
      });

      for (let i = 0; i < data.length; i++) {
        SENSOR_KEYS.forEach(({ key, parse }) => {
          let value = data[i][key];
          if (value !== undefined && value !== null) {
            if (typeof value === "string" || typeof value === "number") {
              value = parse(value);
              data[i][key] = value;
              updateStats(stats[key], value);
            }
          }
        });
      }

      // Finalize stats (average and median)
      SENSOR_KEYS.forEach(({ key }) => {
        // Only round median for "Outdoor temperature"
        finalizeStats(stats[key], data.length, key === "Outdoor temperature");
      });

      dispatch({
        type: "SET_DATA",
        payload: {
          [deviceId]: {
            data,
            pressure: stats.pressure,
            humidity: stats.humidity,
            humidity2: stats.humidity2,
            temperature: stats.temperature,
            temperature2: stats.temperature2,
            co2: stats.co2,
            voltage: stats.voltage,
            current: stats.current,
            power: stats.power,
            "Compressor speed RPM": stats["Compressor speed RPM"],
            "Brine in": stats["Brine in"],
            "Brine out": stats["Brine out"],
            "Discharge pipe temperature": stats["Discharge pipe temperature"],
            "Outdoor temperature": stats["Outdoor temperature"],
            "Tap water top": stats["Tap water top"],
            "Tap water bottom": stats["Tap water bottom"],
          },
          busy: false,
        },
      });
    }
  } else {
    dispatch({
      type: "SET_DATA",
      payload: {
        [deviceId]: {
          data: []
        },
        busy: false,
      },
    });
  }
};

const updateChartData = () => {
  return async (dispatch, getState) => {
    const { token } = getState().loginState;
    try {
      let res = await fetch(backendServer + "getlatest", {
        method: "GET",
        headers: {
          "x-access-token": token,
        },
      });
      if (res.status === 200) {
        let data = await res.json();
          // Deep clone to avoid mutating Redux state later
          const clonedData = Array.isArray(data)
            ? JSON.parse(JSON.stringify(data))
            : data;
        for (let i = 0; i < clonedData.length; i++) {
          if (clonedData && clonedData[i] !== null) {
            dispatch({
              type: "ADD_SENSOR_FILTER",
              sensor: clonedData[i].deviceid,
            });
            updateChart(clonedData[i].deviceid, dispatch, getState);
          }
        }
        dispatch({
          type: "SET_DATA",
          payload: {
            data: clonedData,
          },
        });
      } else if (res.status === 401) {
        dispatch(LoginAction.logout());
      } else {
        console.log("Error:", res);
      }
    } catch (error) {
      dispatch(LoginAction.logout());
      console.log("Error:", error);
    }
  };
};

const changeApi = (deviceid) => {
  return async (dispatch, getState) => {
    const { token } = getState().loginState;
    try {
      let res = await fetch(backendServer + "changeapi", {
        method: "POST",
        headers: {
          "x-access-token": token,
        },
        body: JSON.stringify({
          deviceid,
        }),    
      });
      if (res.status === 200) {
        let data = await res.json();
          dispatch({
            type: "API_CHANGED",
            payload: {
              deviceid,
              apikey: data.api,
            }
          });
      } else if (res.status === 401) {
        dispatch(LoginAction.logout());
      } else {
        console.log("Error:", res);
      }
    } catch (error) {
      console.log("Error:", error);
    }
  };
};


const loadMinMax = (deviceId, fromDate, toDate) => {
  return async (dispatch, getState) => {
    if (typeof fromDate === "string") {
      fromDate = new moment(fromDate, "D.M.YYYY HH.mm");
    }
    if (typeof toDate === "string") {
      toDate = new moment(toDate, "D.M.YYYY HH.mm");
    }

    const { token } = getState().loginState;
    try {
      let res = await fetch(backendServer + "getMinMax", {
        method: "POST",
        "Content-Type": "application/json",
        headers: {
          "x-access-token": token,
        },
        body: JSON.stringify({
          deviceId,
          fromDate: fromDate.valueOf(),
          toDate: toDate.valueOf(),
        }),
      });
      if (res.status === 200) {
        let data = await res.json();
        dispatch({
          type: "SET_MINMAX_DATA",
          payload: {
            deviceId,
            data,
          },
        });
      } else if (res.status === 401) {
        dispatch(LoginAction.logout());
      } else {
        console.log("Error:", res);
      }
    } catch (error) {
      dispatch(LoginAction.logout());
      console.log("Error:", error);
    }
  };
};

export const DataAction = {
  toggleDetailedValues,
  updateChartData,
  getDevices,
  updateChart,
  loadMinMax,
  changeApi,
};
